/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form } from 'react-bootstrap';
import {
  useForm,
  FieldValues,
  SubmitHandler,
  Controller
} from 'react-hook-form';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  faArrowLeftLong,
  faArrowRightLong
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import DocPageHeader from 'components/docs/DocPageHeader';
import Button from 'components/base/Button';
import { showAlert } from 'store/alert/alert.thunk';
import currencyService from 'service/currency';
import legalEntityCurrencyRateService from 'service/legalEntityCurrencyRate';
import { useWindowWidth } from 'utils/useWindowSize';

interface CreateCurrencyExchangeRateProps {
  setOpenConversion: (show: boolean) => void;
  refetch?: any;
}

const CreateCurrencyExchangeRate = ({
  setOpenConversion,
  refetch
}: CreateCurrencyExchangeRateProps) => {
  const { t, i18n } = useTranslation();
  const windowSize: any = useWindowWidth();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({});

  const dispatch: Dispatch<any> = useDispatch();
  const inputRef = useRef<HTMLInputElement | any>();
  const inputRefSell = useRef<HTMLInputElement | any>();

  const [loading, setLoading] = useState(false);
  const [mainCurrencyOptions, setMainCurrencyOptions] = useState<any[]>([]);
  const [unitsOptions, setUnitsOptions] = useState<any[]>([]);
  const [originalUnitsOptions, setOriginalUnitsOptions] = useState<any>([]);
  const [selectedMainCurrency, setSelectedMainCurrency] = useState<any>(null);

  const [mainCurrencyOptionsSell, setMainCurrencyOptionsSell] = useState<any[]>(
    []
  );
  const [unitsOptionsSell, setUnitsOptionsSell] = useState<any[]>([]);
  const [originalUnitsOptionsSell, setOriginalUnitsOptionsSell] = useState<any>(
    []
  );
  const [selectedMainCurrencySell, setSelectedMainCurrencySell] =
    useState<any>(null);

  useQuery(['GET_CURRENCY'], async () => {
    await currencyService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.currencies?.map((currency: any) => ({
          value: currency.id,
          pid: currency?.pid || 0,
          label: currency.name?.[i18n?.language]
        }));
        setMainCurrencyOptions(options);
        setUnitsOptions(options);
        setOriginalUnitsOptions(options);

        setMainCurrencyOptionsSell(options);
        setUnitsOptionsSell(options);
        setOriginalUnitsOptionsSell(options);
      });
  });

  const onSubmit: SubmitHandler<FieldValues> = data => {
    createData(data);
  };

  const createData = (data: any) => {
    setLoading(true);

    const selectedCurrencyMain = mainCurrencyOptions.find(
      (option: any) => option.value === data?.main_currency
    );
    const selectedCurrencyUnits = unitsOptions.find(
      (option: any) => option.value === data?.units
    );

    const selectedCurrencyMainSell = mainCurrencyOptionsSell.find(
      (option: any) => option.value === data?.main_currency_sell
    );
    const selectedCurrencyUnitsSell = unitsOptionsSell.find(
      (option: any) => option.value === data?.units_sell
    );
    const createDataBuy = {
      conversion_type: 'BUY',
      income_amount: 1,
      outcome_amount: +data?.convertible_buy || 0,
      income_currency_id: data?.main_currency_buy,
      income_currency_pid: selectedCurrencyMain?.pid,
      outcome_currency_id: data?.units_buy,
      outcome_currency_pid: selectedCurrencyUnits?.pid
    };

    const createDataSell = {
      conversion_type: 'SELL',
      income_amount: 1,
      outcome_amount: +data?.convertible_sell || 0,
      income_currency_id: data?.main_currency_sell,
      income_currency_pid: selectedCurrencyMainSell?.pid,
      outcome_currency_id: data?.units_sell,
      outcome_currency_pid: selectedCurrencyUnitsSell?.pid
    };

    legalEntityCurrencyRateService
      ?.createConversionRate(createDataBuy)
      ?.then((res: any) => {
        if (res) {
          legalEntityCurrencyRateService
            ?.createConversionRate(createDataSell)
            ?.then((resSell: any) => {
              if (resSell) {
                /* prettier-ignore */
                dispatch(
            showAlert({
             title: `${t('currency_exchange_rate')} ${t('successfully_added')}`,
              type: 'success'
            })
          );

                refetch();
                setOpenConversion(false);
                setLoading(false);
              }
            });
        }
      });
  };

  useEffect(() => {
    if (selectedMainCurrency && inputRef.current) {
      inputRef.current.focus();
    } else {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [inputRef.current, selectedMainCurrency]);

  useEffect(() => {
    if (selectedMainCurrencySell && inputRefSell.current) {
      inputRefSell.current.focus();
    } else {
      if (inputRefSell.current) {
        inputRefSell.current.focus();
      }
    }
  }, [inputRefSell.current, selectedMainCurrencySell]);

  return (
    <div>
      <Form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
        <div
          className={
            windowSize > 768
              ? 'd-flex gap-2 justify-content-between mb-3'
              : 'd-flex gap-2 justify-content-between mb-3 flex-column'
          }
        >
          <div className="d-flex align-items-center gap-3">
            <p
              style={{
                marginBottom: '0px',
                fontSize: '14px'
              }}
            >
              {t('buy')}
            </p>
            <FontAwesomeIcon icon={faArrowRightLong} className="text-success" />
          </div>
          <Form.Group className="w-100">
            <Controller
              name="base"
              control={control}
              render={({ field }) => (
                <Form.Floating className="w-100">
                  <Form.Control
                    {...field}
                    disabled
                    defaultValue="1"
                    type="text"
                    placeholder="Base currency"
                    onFocus={e => e.target.select()}
                  />
                  <label htmlFor="floatingInputCustom">
                    {t('unit_base_currency')}
                  </label>
                </Form.Floating>
              )}
            />
          </Form.Group>

          <Form.Group className="w-100">
            <div className="react-select-container">
              <Controller
                name="main_currency_buy"
                control={control}
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select
                      {...field}
                      ref={inputRef}
                      onChange={(e: any) => {
                        const selectedOption = mainCurrencyOptions.find(
                          (option: any) => option.value === e.target.value
                        );

                        if (selectedOption) {
                          setSelectedMainCurrency(selectedOption);

                          const updatedUnitsOptions =
                            originalUnitsOptions.filter(
                              (option: any) =>
                                option.value !== selectedOption?.value
                            );

                          if (updatedUnitsOptions.length === 1) {
                            setUnitsOptions(updatedUnitsOptions);
                            setValue('units_buy', updatedUnitsOptions[0].value); // Automatically select the first available option if length is 2
                          } else {
                            setUnitsOptions(updatedUnitsOptions);
                            setValue('units_buy', ''); // Clear the units select if length is not 2
                          }
                        }

                        field.onChange(e);
                      }}
                      isInvalid={!!errors.main_currency_buy}
                    >
                      <option className="d-none" value=""></option>
                      {mainCurrencyOptions?.map((option: any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">
                      {t('main_currency')}
                    </label>
                  </Form.Floating>
                )}
              />
              {errors?.main_currency_buy && (
                <span
                  style={{ marginTop: '2px', color: 'red', fontSize: '12px' }}
                >
                  {t('required_field')}
                </span>
              )}
            </div>
          </Form.Group>

          <Form.Group>
            <DocPageHeader
              className="d-flex justify-content-center align-items-center"
              title="="
            />
          </Form.Group>

          <Form.Group className="w-100">
            <Controller
              name="convertible_buy"
              control={control}
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Form.Floating className="w-100">
                  <Form.Control
                    {...field}
                    type="text"
                    ref={selectedMainCurrency && inputRef}
                    placeholder="Base convertible"
                    onFocus={e => e.target.select()}
                    autoComplete="off"
                    isInvalid={!!errors?.convertible_buy}
                  />
                  <label htmlFor="floatingInputCustom">
                    {t('convertible_currency')}
                  </label>

                  {errors?.convertible_buy && (
                    <span
                      style={{
                        marginTop: '2px',
                        color: 'red',
                        fontSize: '12px'
                      }}
                    >
                      {t('required_field')}
                    </span>
                  )}
                </Form.Floating>
              )}
            />
          </Form.Group>

          <Form.Group className="w-100">
            <div className="react-select-container">
              <Controller
                name="units_buy"
                control={control}
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select
                      {...field}
                      onChange={(e: any) => {
                        const selectedOption = unitsOptions.find(
                          (option: any) => option.value === e.target.value
                        );

                        if (selectedMainCurrency) {
                          setUnitsOptions(originalUnitsOptions);
                          setSelectedMainCurrency(null);
                        } else {
                          setUnitsOptions(
                            unitsOptions.filter(
                              (option: any) =>
                                option.value !== selectedOption?.value
                            )
                          );
                        }
                        field.onChange(e);
                      }}
                      isInvalid={!!errors.units_buy}
                    >
                      <option className="d-none" value=""></option>
                      {unitsOptions?.map((option: any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <label htmlFor="floatingInputCustom">
                      {t('convertible_currency_unit')}
                    </label>
                  </Form.Floating>
                )}
              />
              {errors?.units_buy && (
                <span
                  style={{ marginTop: '2px', color: 'red', fontSize: '12px' }}
                >
                  {t('required_field')}
                </span>
              )}
            </div>
          </Form.Group>
        </div>

        <div
          className={
            windowSize > 768
              ? 'd-flex gap-2 justify-content-between mb-3'
              : 'd-flex gap-2 justify-content-between mb-3 flex-column'
          }
        >
          <div className="d-flex align-items-center gap-2">
            <p style={{ marginBottom: '0px', fontSize: '14px' }}>{t('sell')}</p>
            <FontAwesomeIcon icon={faArrowLeftLong} className="text-danger" />
          </div>
          <Form.Group className="w-100">
            <Controller
              name="base"
              control={control}
              render={({ field }) => (
                <Form.Floating className="w-100">
                  <Form.Control
                    {...field}
                    disabled
                    defaultValue="1"
                    type="text"
                    placeholder="Base currency"
                    onFocus={e => e.target.select()}
                  />
                  <label htmlFor="floatingInputCustom">
                    {t('unit_base_currency')}
                  </label>
                </Form.Floating>
              )}
            />
          </Form.Group>

          <Form.Group className="w-100">
            <div className="react-select-container">
              <Controller
                name="main_currency_sell"
                control={control}
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select
                      {...field}
                      onChange={(e: any) => {
                        const selectedOption = mainCurrencyOptionsSell.find(
                          (option: any) => option.value === e.target.value
                        );

                        if (selectedOption) {
                          setSelectedMainCurrencySell(selectedOption);

                          const updatedUnitsOptionsSell =
                            originalUnitsOptionsSell.filter(
                              (option: any) =>
                                option.value !== selectedOption?.value
                            );

                          if (updatedUnitsOptionsSell.length === 1) {
                            setUnitsOptionsSell(updatedUnitsOptionsSell);
                            setValue(
                              'units_sell',
                              updatedUnitsOptionsSell[0].value
                            ); // Automatically select the first available option if length is 2
                          } else {
                            setUnitsOptionsSell(updatedUnitsOptionsSell);
                            setValue('units_sell', ''); // Clear the units select if length is not 2
                          }
                        }

                        field.onChange(e);
                      }}
                      isInvalid={!!errors.main_currency_sell}
                    >
                      <option className="d-none" value=""></option>
                      {mainCurrencyOptionsSell?.map((option: any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">
                      {t('main_currency')}
                    </label>
                  </Form.Floating>
                )}
              />
              {errors?.main_currency_sell && (
                <span
                  style={{ marginTop: '2px', color: 'red', fontSize: '12px' }}
                >
                  {t('required_field')}
                </span>
              )}
            </div>
          </Form.Group>

          <Form.Group>
            <DocPageHeader
              className="d-flex justify-content-center align-items-center"
              title="="
            />
          </Form.Group>

          <Form.Group className="w-100">
            <Controller
              name="convertible_sell"
              control={control}
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Form.Floating className="w-100">
                  <Form.Control
                    {...field}
                    type="text"
                    ref={selectedMainCurrencySell && inputRefSell}
                    placeholder="Base convertible"
                    onFocus={e => e.target.select()}
                    autoComplete="off"
                    isInvalid={!!errors?.convertible_sell}
                  />
                  <label htmlFor="floatingInputCustom">
                    {t('convertible_currency')}
                  </label>

                  {errors?.convertible_sell && (
                    <span
                      style={{
                        marginTop: '2px',
                        color: 'red',
                        fontSize: '12px'
                      }}
                    >
                      {t('required_field')}
                    </span>
                  )}
                </Form.Floating>
              )}
            />
          </Form.Group>

          <Form.Group className="w-100">
            <div className="react-select-container">
              <Controller
                name="units_sell"
                control={control}
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select
                      {...field}
                      onChange={(e: any) => {
                        const selectedOption = unitsOptionsSell.find(
                          (option: any) => option.value === e.target.value
                        );

                        if (selectedMainCurrencySell) {
                          setUnitsOptionsSell(originalUnitsOptions);
                          setSelectedMainCurrencySell(null);
                        } else {
                          setUnitsOptionsSell(
                            unitsOptionsSell.filter(
                              (option: any) =>
                                option.value !== selectedOption?.value
                            )
                          );
                        }
                        field.onChange(e);
                      }}
                      isInvalid={!!errors.units_sell}
                    >
                      <option className="d-none" value=""></option>
                      {unitsOptionsSell?.map((option: any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <label htmlFor="floatingInputCustom">
                      {t('convertible_currency_unit')}
                    </label>
                  </Form.Floating>
                )}
              />
              {errors?.units_sell && (
                <span
                  style={{ marginTop: '2px', color: 'red', fontSize: '12px' }}
                >
                  {t('required_field')}
                </span>
              )}
            </div>
          </Form.Group>
        </div>

        <Button
          className="w-100"
          variant="primary"
          type="submit"
          loading={loading}
          loadingPosition="start"
        >
          {t('save')}
        </Button>
      </Form>
    </div>
  );
};

export default CreateCurrencyExchangeRate;
